'use client'

import { useTranslations } from 'next-intl'
import { Receipt, FileSpreadsheet, Building2, TrendingUp } from 'lucide-react'
import { motion } from 'framer-motion'

interface FactorCardProps {
  icon: React.ElementType
  title: string
  description: string
}

const FactorCard = ({ icon: Icon, title, description }: FactorCardProps) => (
  <motion.div
    variants={item}
    className="flex flex-col items-center text-center p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
  >
    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-3 md:mb-4">
      <Icon className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
    </div>
    <h3 className="text-lg md:text-xl font-semibold mb-2">{title}</h3>
    <p className="text-sm md:text-base text-muted-foreground">{description}</p>
  </motion.div>
)

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
}

const item = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { opacity: 1, scale: 1 }
}

export const FAQ = () => {
  const t = useTranslations('faq')

  return (
    <div className="w-full bg-muted/50">
      <section className="container py-8 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl font-bold mb-4"
          >
            {t('mainQuestion')}
          </motion.h2>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mb-8 md:mb-12"
        >
          <FactorCard
            icon={Receipt}
            title={t('factors.taxation.title')}
            description={t('factors.taxation.description')}
          />
          <FactorCard
            icon={FileSpreadsheet}
            title={t('factors.accounting.title')}
            description={t('factors.accounting.description')}
          />
          <FactorCard
            icon={Building2}
            title={t('factors.address.title')}
            description={t('factors.address.description')}
          />
          <FactorCard
            icon={TrendingUp}
            title={t('factors.turnover.title')}
            description={t('factors.turnover.description')}
          />
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          className="max-w-3xl mx-auto space-y-4 text-muted-foreground px-4 md:px-0"
        >
          {(t.raw('additionalInfo') as string[]).map((text: string, index: number) => (
            <motion.p key={index} variants={item} className="text-center text-sm md:text-base">
              {text}
            </motion.p>
          ))}
        </motion.div>
      </section>
    </div>
  )
}
